import { ACTION_TYPES, DECISION_EXTRA_BRANCHES, DECISION_MAX_OPTIONS, DECISION_PROVIDERS, DECISION_QUESTION_TYPES, HTTP_AUTH_TYPES, HTTP_REQUEST_METHODS, HTTP_RESPONSE_TYPES, TRIGGER_SUBTYPES, WEBHOOK_METHODS, decisionRoutedQuestion, decisionValueHandles, } from "./agent-workflow.js";
export { HTTP_AUTH_TYPES, HTTP_REQUEST_METHODS, HTTP_RESPONSE_TYPES, WEBHOOK_METHODS };
/**
 * Workflow capability catalogue + structural graph validator.
 *
 * This file is the single source of truth for "what a workflow can contain".
 * It is consumed by:
 *   - the UI config forms (what fields to render per node),
 *   - the chat-driven authoring skill (so an agent knows what's possible
 *     before it emits a graph — see buildWorkflowCapabilityCatalogue),
 *   - server-side validation at run-start and at authoring time
 *     (validateWorkflowGraph below + the semantic checks in
 *     server/services/workflow-validate.ts).
 *
 * Keep it framework-free (no zod-runtime, no DB) so both the browser bundle
 * and the agent-facing catalogue endpoint can import it cheaply.
 */
// --------------------------------------------------------------------
// Condition operators (must mirror server/services/workflow-jsonlogic.ts)
// --------------------------------------------------------------------
export const WORKFLOW_CONDITION_OPERATORS = [
    "==",
    "!=",
    ">",
    ">=",
    "<",
    "<=",
    "and",
    "or",
    "!",
    "var",
];
/** Whole-run safety guard for loops and unexpectedly long graphs. */
export const MAX_WORKFLOW_STEP_EXECUTIONS = 100;
/** Default left-to-right spacing used when an agent authors a canvas graph. */
export const WORKFLOW_DEFAULT_HORIZONTAL_GAP_PX = 300;
// --------------------------------------------------------------------
// Trigger catalogue
// --------------------------------------------------------------------
export const WORKFLOW_TRIGGER_CATALOGUE = [
    {
        subtype: "manual",
        label: "Manual",
        description: "Started by a person clicking Run (or via the start-run API). No schedule.",
        configFields: [],
        implemented: true,
    },
    {
        subtype: "schedule",
        label: "Schedule (cron)",
        description: "Fires on a cron schedule. The platform owns the timer (node-cron); the assigned agent is woken on each tick. Independent of the agent runtime's own cron support.",
        configFields: [
            {
                key: "cronExpression",
                label: "Cron expression",
                type: "cron",
                required: true,
                description: "Standard 5- or 6-field cron, e.g. '0 9 * * 1' for 09:00 every Monday.",
            },
            {
                key: "timezone",
                label: "Timezone",
                type: "string",
                required: false,
                description: "IANA tz name, e.g. 'Europe/London'. Defaults to UTC.",
            },
        ],
        implemented: true,
    },
    {
        subtype: "webhook",
        label: "Inbound webhook",
        description: "Fires when an external system calls the workflow's webhook URL with the shared secret. Defaults to POST. GET reads inputs from the URL query string; POST, PUT, PATCH, and DELETE read a JSON request body.",
        configFields: [
            {
                key: "webhookId",
                label: "Webhook id",
                type: "string",
                required: false,
                description: "Assigned by the platform when the workflow is activated. Read-only.",
            },
            {
                key: "method",
                label: "HTTP method",
                type: "string",
                required: false,
                description: `HTTP method the webhook URL accepts: ${WEBHOOK_METHODS.join(", ")}. Defaults to POST. GET reads the URL query string (keep it non-sensitive); the other methods read a JSON request body.`,
            },
        ],
        implemented: true,
    },
    {
        subtype: "channel_message",
        label: "Channel message",
        description: "Fires when a message is posted to a watched agent channel. The message text becomes the run input `message`.",
        configFields: [
            {
                key: "channelId",
                label: "Channel",
                type: "uuid",
                required: true,
                description: "The agent channel to watch for new messages.",
            },
        ],
        implemented: true,
    },
    {
        subtype: "email_received",
        label: "Outlook email received",
        description: "Fires when the selected agent's verified Outlook mailbox receives a new Inbox email. Optional filters narrow which messages start a run.",
        configFields: [
            {
                key: "agentId",
                label: "Agent mailbox",
                type: "uuid",
                required: true,
                description: "Agent whose verified bound Outlook mailbox is watched. The mailbox address is resolved by the platform and is not supplied directly.",
            },
            {
                key: "from",
                label: "Sender email",
                type: "string",
                required: false,
                description: "Only start for messages from this exact email address.",
            },
            {
                key: "subjectContains",
                label: "Subject contains",
                type: "string",
                required: false,
                description: "Only start when the subject contains this text.",
            },
            {
                key: "includeAttachments",
                label: "Include attachments",
                type: "boolean",
                required: false,
                description: "When true, include attachment metadata and authenticated download paths in inputs.email.attachments. File bytes remain in Outlook until requested.",
            },
        ],
        implemented: true,
    },
];
// --------------------------------------------------------------------
// Action catalogue
// --------------------------------------------------------------------
export const WORKFLOW_ACTION_CATALOGUE = [
    {
        actionType: "mcp_tool",
        label: "MCP tool call",
        description: "Invoke an MCP tool. By default the executing agent calls it (any tool the agent has access to). Set executor to 'platform' to have the platform call an HTTP/SSE tenant MCP directly with no agent wake — use this before or after a Decision node to fetch a record or apply its result in a second.",
        configFields: [
            {
                key: "tool",
                label: "Tool name",
                type: "string",
                required: true,
                description: "Fully-qualified MCP tool name, '<server>.<tool>', e.g. 'proclaw-agent-comms.teams_send_message'. The server prefix is the tenant MCP's name as the agent sees it.",
            },
            {
                key: "args",
                label: "Arguments (JSON)",
                type: "json",
                required: false,
                description: "JSON object of arguments for the tool. May reference prior step outputs via {{steps.<id>.outputs.<key>}}.",
            },
            {
                key: "executor",
                label: "Executed by",
                type: "string",
                required: false,
                description: "'agent' (default) wakes the assigned agent to call the tool. 'platform' calls the tenant MCP from the platform itself: only for HTTP/SSE tenant MCPs, only with plain templated args (no {\"$file\": …} references), and the tool must be allowed on the tenant MCP. Outputs: { result (parsed JSON when the tool returned JSON text), raw, isError, latencyMs }.",
            },
            {
                key: "onToolError",
                label: "If the tool reports an error",
                type: "string",
                required: false,
                description: "Platform executor only. 'fail' (default) fails the run so the error shows on the timeline and can be retried. 'continue' completes the step with outputs.isError = true so a Condition can branch on it.",
            },
        ],
        implemented: true,
    },
    {
        actionType: "http_request",
        label: "HTTP Request",
        description: "Send an HTTP request directly from the platform. Defaults to POST and does not require an Agent node.",
        configFields: [
            {
                key: "method",
                label: "Method",
                type: "string",
                required: false,
                description: `HTTP method: ${HTTP_REQUEST_METHODS.join(", ")}. Defaults to POST.`,
            },
            {
                key: "url",
                label: "URL",
                type: "string",
                required: true,
                description: "Absolute HTTPS URL to request.",
            },
            {
                key: "body",
                label: "Body (JSON)",
                type: "json",
                required: false,
                description: "JSON body to send.",
            },
            {
                key: "responseType",
                label: "Response type",
                type: "string",
                required: false,
                description: `How to handle the response: ${HTTP_RESPONSE_TYPES.join(", ")}. Defaults to auto. PDF and image responses are saved to the assigned agent's workspace.`,
            },
            {
                key: "auth",
                label: "Credential",
                type: "json",
                required: false,
                description: `Optional credential, as { secretId, type, name }. secretId is an agent secret in this tenant. type is one of ${HTTP_AUTH_TYPES.join(", ")} and defaults to bearer; name is the header or query-parameter name and is required unless the type is bearer. Store only the id: the value is read from the vault at run time and never enters the graph. Omit the whole object to call an endpoint that needs no credential.`,
            },
        ],
        implemented: true,
    },
    {
        actionType: "send_email",
        label: "Send email",
        description: "Send an email via the executing agent's mail tool. (Convenience wrapper over an MCP tool.)",
        configFields: [
            { key: "to", label: "To", type: "string", required: true, description: "Recipient address." },
            { key: "cc", label: "CC", type: "string", required: false, description: "Optional CC recipients, separated by commas or semicolons." },
            { key: "subject", label: "Subject", type: "string", required: true, description: "Email subject." },
            { key: "body", label: "Body", type: "text", required: true, description: "Email body (markdown ok)." },
        ],
        implemented: true,
    },
    {
        actionType: "wait_for_email_reply",
        label: "Wait for email reply",
        description: "Park the workflow until the assigned agent's mailbox receives a reply or the fixed deadline passes, then continue through its outgoing edge.",
        configFields: [
            { key: "messageId", label: "Message id", type: "string", required: false, description: "Immutable Graph message id returned by the send step." },
            { key: "conversationId", label: "Conversation id", type: "string", required: false, description: "Graph conversation id returned by the send step." },
            { key: "internetMessageId", label: "Internet message id", type: "string", required: false, description: "Message-ID returned by the send step." },
            { key: "expectedFrom", label: "Expected sender", type: "string", required: true, description: "Sender address required before a reply can be treated as actionable." },
            { key: "sentAt", label: "Sent at", type: "string", required: true, description: "Original send timestamp in ISO 8601 format." },
            { key: "waitDuration", label: "Wait duration", type: "number", required: false, description: "Positive whole-number duration. Required with waitUnit for new workflows." },
            { key: "waitUnit", label: "Wait unit", type: "string", required: false, description: "minutes, hours, or days. Required with waitDuration for new workflows." },
            { key: "ignoredMessageIds", label: "Ignored message ids", type: "json", required: false, description: "Previously handled reply ids, such as an out-of-office response." },
        ],
        implemented: true,
    },
    {
        actionType: "post_to_channel",
        label: "Post to channel",
        description: "Post a message into an agent channel.",
        configFields: [
            { key: "channelId", label: "Channel id", type: "uuid", required: true, description: "Target agent channel." },
            { key: "message", label: "Message", type: "text", required: true, description: "Message text (markdown ok)." },
        ],
        implemented: true,
    },
];
// --------------------------------------------------------------------
// Node catalogue
// --------------------------------------------------------------------
export const WORKFLOW_NODE_CATALOGUE = [
    {
        type: "trigger",
        label: "Trigger",
        description: "Entry point. Exactly one per workflow. Its subtype decides how the run starts. Use for the 'when' of the request: a time or cadence is schedule, an inbound call is webhook, an Outlook Inbox message is email_received, a channel post is channel_message, run-by-hand is manual.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "subtype", label: "Trigger type", type: "string", required: true, description: `One of: ${TRIGGER_SUBTYPES.join(", ")}.` },
        ],
        outgoing: "single",
    },
    {
        type: "agent",
        label: "Agent step",
        description: "A prompt for the assigned agent to execute, producing structured output the next steps can read. Use whenever a step needs judgement, writing, summarising, extracting, scoring or deciding wording (e.g. 'summarise the tickets', 'score the lead').",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "agentId", label: "Agent", type: "uuid", required: true, description: "The agent that executes this step." },
            { key: "promptTemplate", label: "Prompt template", type: "text", required: true, description: "Instructions for the agent. May reference {{input}} and {{steps.<id>.outputs.<key>}}." },
            { key: "model", label: "Model", type: "string", required: false, description: "Optional model override for this step — a modelId from the catalogue (see the `models` list returned by get_workflow_capabilities). Omit to run on the agent's own configured model." },
            { key: "fallbackModel", label: "Backup model", type: "string", required: false, description: "Optional backup model used when the primary model fails or the step is retried. The agent's own configured model is always the final fallback." },
            { key: "contextMode", label: "Session context", type: "string", required: false, description: "'fresh' (default) starts this step in a new agent session holding only its prompt, inputs and procedures; earlier steps reach it through {{steps.<id>.outputs.<key>}}. 'continue' runs it on in the session that completed the previous step — only for a step that genuinely needs that conversation, since every turn then re-reads all of it." },
        ],
        outgoing: "single",
    },
    {
        type: "condition",
        label: "Condition",
        description: "Evaluated server-side over prior step outputs. Supports yes/no or ordered multiple-choice branches. Use only when the user describes a decision or an 'only if / otherwise' split; skip it for a straight-through flow.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "mode", label: "Branching mode", type: "string", required: false, description: "Either binary (default) or multiple." },
            { key: "expression", label: "Expression", type: "json", required: false, description: `Binary-mode JSONLogic over {inputs, steps}. Operators: ${WORKFLOW_CONDITION_OPERATORS.join(" ")}.` },
            { key: "choices", label: "Choices", type: "json", required: false, description: "Multiple-mode ordered choices: {id, label, expression}; exactly one choice must set isDefault=true and needs no expression." },
        ],
        outgoing: "branch",
    },
    {
        type: "action",
        label: "Action",
        description: "A side-effect with no thinking: call a tool, post a message, send an email, hit a webhook. Use for the 'then do X' that touches the outside world; keep all reasoning in an agent step before it.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "actionType", label: "Action type", type: "string", required: true, description: `One of: ${ACTION_TYPES.join(", ")}.` },
        ],
        outgoing: "single",
    },
    {
        type: "decision",
        label: "Decision",
        description: "A fast, cheap, platform-executed classification or gate (TypeSafe Jev). Sends a few named state fields to a decision model that answers typed questions — pick one option, yes/no, or a rubric score — with probabilities, never text. Runs in about a second for a fraction of a cent and never wakes an agent. Use it BEFORE an agent step to route or gate ('is this spam?', 'which team?', 'proceed?'), or AFTER one to map free-text output onto a fixed set of values. It cannot write anything: pair it with an agent step for text.",
        configFields: [
            { key: "label", label: "Label", type: "string", required: false, description: "Display name." },
            { key: "provider", label: "Provider", type: "string", required: false, description: `One of: ${DECISION_PROVIDERS.join(", ")}. Defaults to typesafe. The tenant must have that provider's API key on file (Settings → Models).` },
            { key: "model", label: "Model", type: "string", required: false, description: "Provider model alias. Omit for the provider default (jev-latest)." },
            { key: "state", label: "State", type: "json", required: true, description: "Object of named inputs the decision looks at, e.g. { subject: \"{{input.subject}}\", message: \"{{input.body}}\" }. Values are templates over {{input.*}} and {{steps.<id>.outputs.<key>}}. Send only what the decision needs: accuracy falls as unrelated text grows. Text only, no attachments." },
            { key: "questions", label: "Questions", type: "json", required: true, description: `Array of { id, type, instructions, ... }. type is one of ${DECISION_QUESTION_TYPES.join(", ")}. choice: options [{ value, description }] (max ${DECISION_MAX_OPTIONS}) — the description carries the meaning, value is what edges and later steps see; optionally includeNone + noneFloor. noul: a yes/no question, optional trueDescription/falseDescription. score: levels [] of ordered concrete situations (2 to 10). Independent questions in one node run in parallel at no extra cost; a question that depends on another's answer needs a second Decision node.` },
            { key: "route", label: "Routing", type: "json", required: false, description: `{ question, minConfidence, yesThreshold, noThreshold, fallbackBranch }. question = the id whose answer drives the edges (default: first question). Each option value (or yes/no, or a score level index) is an outgoing sourceHandle. Extra handles: ${DECISION_EXTRA_BRANCHES.join(", ")}; when one has no edge, fallbackBranch (a value handle) is taken instead. Set fallbackBranch to the safe path so a provider outage or an unsure answer never blocks the run.` },
            { key: "timeoutMs", label: "Timeout (ms)", type: "number", required: false, description: "Per-request timeout, default 20000. Transient provider errors are retried with backoff before the unavailable branch is taken." },
        ],
        outgoing: "branch",
    },
];
/**
 * Authoring guidance for the decision node, served with the capability
 * catalogue so an agent building a workflow knows how to use it well. Written
 * from the measured lessons of the ConnectWise AutoTriage rollout.
 */
export const WORKFLOW_DECISION_GUIDANCE = "Decision nodes decide, they never write. Put the meaning in each option's description (an ordered test or a definition with examples), not in its value. Always give a choice question includeNone=true with a noneFloor around 0.5 so 'nothing fits' is a real answer, and keep it separate from a legitimate 'not needed' option. Confidence measures how concentrated the answer was, not whether it was right: start minConfidence at 0.85 for anything that closes, deletes or sends, lower for read-only routing, and set route.fallbackBranch to the safe path (usually the agent review) so low_confidence, none and unavailable never block a run. State is the only thing the model sees: send the few fields the decision needs, never whole records. One node may ask several independent questions in one call; a question whose options depend on another answer belongs in a second Decision node with optionsFrom bound to the first's outputs. When a step would both decide something and write something, and a later branch depends on the decision, put a Decision node first and pass its answer to the agent step, so the branch runs on a typed answer rather than on a field the agent chose to emit. Downstream steps read steps.<id>.outputs.branch and steps.<id>.outputs.answers.<questionId>.value / .confidence / .p.";
/**
 * Assemble the full machine-readable catalogue. Served to agents (the
 * authoring skill reads this so it knows exactly what to emit) and to the
 * UI. Stable shape — additive changes only.
 */
export function buildWorkflowCapabilityCatalogue() {
    return {
        nodeTypes: WORKFLOW_NODE_CATALOGUE,
        triggers: WORKFLOW_TRIGGER_CATALOGUE,
        actions: WORKFLOW_ACTION_CATALOGUE,
        conditionOperators: WORKFLOW_CONDITION_OPERATORS,
        decisionGuidance: WORKFLOW_DECISION_GUIDANCE,
        layout: {
            horizontalGapPx: WORKFLOW_DEFAULT_HORIZONTAL_GAP_PX,
            axis: "x",
            description: "Place consecutive nodes 300px apart on the x axis by default. This is initial layout guidance only; users can freely reposition nodes on the canvas.",
        },
        edge: {
            description: "Edges connect node outputs to node inputs. Binary conditions use sourceHandle 'yes' | 'no'; multiple-choice conditions use a choice id; decision nodes use an option value (or 'yes'/'no' for a noul, a level index for a score) plus the optional 'none' | 'low_confidence' | 'unavailable' handles; all other nodes leave sourceHandle null.",
            fields: ["id", "source", "target", "sourceHandle", "label", "animated"],
        },
        loops: {
            description: `Loops are allowed when a condition inside the loop has a connected branch that exits it. Runs stop after ${MAX_WORKFLOW_STEP_EXECUTIONS} total step executions.`,
            maxStepExecutions: MAX_WORKFLOW_STEP_EXECUTIONS,
        },
    };
}
/**
 * Pure structural validation of a workflow graph. Catches the classes of
 * problem we can detect without the DB: missing/duplicate trigger, dangling
 * edges, unreachable nodes, missing required node config, unsafe loops, and
 * condition branches with no handler.
 *
 * Semantic checks that need the DB (agent exists in tenant, condition
 * expression parses with the real evaluator) live in
 * server/services/workflow-validate.ts and are layered on top of this.
 *
 * Returns [] when the graph is runnable. `errors` block execution;
 * `warnings` are advisory.
 */
export function validateWorkflowGraph(graph) {
    const issues = [];
    const nodes = graph.nodes ?? [];
    const edges = graph.edges ?? [];
    if (nodes.length === 0) {
        issues.push({
            level: "error",
            code: "empty_graph",
            message: "Workflow has no nodes. Add a trigger and at least one step.",
        });
        return issues;
    }
    // Duplicate node ids.
    const seen = new Set();
    for (const n of nodes) {
        if (seen.has(n.id)) {
            issues.push({
                level: "error",
                code: "duplicate_node_id",
                message: `Duplicate node id "${n.id}".`,
                nodeId: n.id,
            });
        }
        seen.add(n.id);
    }
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    // Trigger cardinality.
    const triggers = nodes.filter((n) => n.type === "trigger");
    if (triggers.length === 0) {
        issues.push({
            level: "error",
            code: "no_trigger",
            message: "Workflow has no trigger node. Every workflow needs exactly one.",
        });
    }
    else if (triggers.length > 1) {
        issues.push({
            level: "warning",
            code: "multiple_triggers",
            message: `Workflow has ${triggers.length} trigger nodes; only the first is used.`,
            nodeId: triggers[1].id,
        });
    }
    // Edge endpoints must reference real nodes.
    for (const e of edges) {
        if (!nodeById.has(e.source)) {
            issues.push({
                level: "error",
                code: "dangling_edge_source",
                message: `Edge "${e.id}" starts from a node that doesn't exist ("${e.source}").`,
                edgeId: e.id,
            });
        }
        if (!nodeById.has(e.target)) {
            issues.push({
                level: "error",
                code: "dangling_edge_target",
                message: `Edge "${e.id}" points to a node that doesn't exist ("${e.target}").`,
                edgeId: e.id,
            });
        }
    }
    // Per-node config requirements.
    for (const n of nodes) {
        const data = (n.data ?? {});
        if (n.type === "trigger") {
            const subtype = data.subtype ?? "manual";
            if (subtype === "schedule" && !str(data.cronExpression)) {
                issues.push({
                    level: "error",
                    code: "schedule_missing_cron",
                    message: `Schedule trigger "${label(n)}" has no cron expression.`,
                    nodeId: n.id,
                });
            }
            if (subtype === "channel_message" && !str(data.channelId)) {
                issues.push({
                    level: "error",
                    code: "channel_trigger_missing_channel",
                    message: `Channel-message trigger "${label(n)}" has no channel selected.`,
                    nodeId: n.id,
                });
            }
            if (subtype === "email_received" && !str(data.agentId)) {
                issues.push({
                    level: "error",
                    code: "email_trigger_missing_agent",
                    message: `Outlook email trigger "${label(n)}" has no agent mailbox selected.`,
                    nodeId: n.id,
                });
            }
            if (subtype === "webhook" &&
                str(data.method) &&
                !WEBHOOK_METHODS.includes(String(data.method).toUpperCase())) {
                issues.push({
                    level: "error",
                    code: "webhook_invalid_method",
                    message: `Webhook trigger "${label(n)}" has an unsupported method "${String(data.method)}". Use ${WEBHOOK_METHODS.join(" or ")}.`,
                    nodeId: n.id,
                });
            }
            const outgoing = edges.filter((e) => e.source === n.id);
            if (triggers[0]?.id === n.id && outgoing.length === 0) {
                issues.push({
                    level: "error",
                    code: "trigger_no_outgoing",
                    message: "The trigger has no outgoing connection — there's nothing to run.",
                    nodeId: n.id,
                });
            }
        }
        if (n.type === "agent" && !str(data.agentId)) {
            issues.push({
                level: "error",
                code: "agent_node_missing_agent",
                message: `Agent step "${label(n)}" has no agent selected.`,
                nodeId: n.id,
            });
        }
        if (n.type === "action") {
            const savedActionType = data.actionType ?? "";
            // Existing saved workflows used separate HTTP GET/POST action ids.
            // Validate them through the single HTTP Request contract until edited.
            const actionType = savedActionType === "http_get" || savedActionType === "http_post"
                ? "http_request"
                : savedActionType;
            const config = (data.config ?? {});
            const spec = WORKFLOW_ACTION_CATALOGUE.find((s) => s.actionType === actionType);
            if (!spec) {
                issues.push({
                    level: "error",
                    code: "unknown_action_type",
                    message: `Action "${label(n)}" has an unknown action type "${actionType}".`,
                    nodeId: n.id,
                });
            }
            else {
                for (const f of spec.configFields) {
                    if (f.required && config[f.key] == null) {
                        issues.push({
                            level: "error",
                            code: "action_missing_config",
                            message: `Action "${label(n)}" (${spec.label}) is missing required field "${f.key}".`,
                            nodeId: n.id,
                        });
                    }
                }
                if (actionType === "http_request" &&
                    config.url != null) {
                    const rawUrl = str(config.url).trim();
                    // A URL that is nothing but one variable is legitimate: an upstream
                    // step or webhook payload often hands over a complete link, so there
                    // is no host to write down at authoring time. Nothing can be checked
                    // statically, so it passes here and the runtime enforces it instead —
                    // assertPublicHttpsUrl rejects non-HTTPS, embedded credentials,
                    // private ranges and metadata hosts, on the resolved URL and on every
                    // redirect hop.
                    const isWholeUrlVariable = /^\{\{\s*[A-Za-z0-9_.-]+\s*\}\}$/.test(rawUrl);
                    let validUrl = isWholeUrlVariable;
                    if (!isWholeUrlVariable && rawUrl) {
                        try {
                            const parsed = new URL(rawUrl);
                            const authority = rawUrl.match(/^https:\/\/([^/]+)/i)?.[1] ?? "";
                            validUrl =
                                parsed.protocol === "https:" &&
                                    !parsed.username &&
                                    !parsed.password &&
                                    // A partially variable host cannot work: values substituted into
                                    // a fixed-host URL are percent-encoded, and an encoded host does
                                    // not resolve. Use one variable for the whole URL instead.
                                    !authority.includes("{{");
                        }
                        catch {
                            validUrl = false;
                        }
                    }
                    if (!validUrl) {
                        issues.push({
                            level: "error",
                            code: "http_action_url_invalid",
                            message: `Action "${label(n)}" needs an absolute HTTPS URL, or a single variable holding the whole URL such as {{webhook.downloadJsonUrl}}. A fixed URL may use variables in its path or query string, but not in the host.`,
                            nodeId: n.id,
                        });
                    }
                    // Optional credential. The canvas stores only which secret to use;
                    // the value is read from Key Vault at run time and never travels
                    // through the graph.
                    const auth = config.auth;
                    if (auth && typeof auth === "object") {
                        const a = auth;
                        const secretId = str(a.secretId).trim();
                        const authType = str(a.type ?? "bearer").trim().toLowerCase();
                        const authName = str(a.name).trim();
                        if (secretId) {
                            if (!HTTP_AUTH_TYPES.includes(authType)) {
                                issues.push({
                                    level: "error",
                                    code: "http_action_auth_type_invalid",
                                    message: `Action "${label(n)}" has an unsupported credential type "${authType}". Use ${HTTP_AUTH_TYPES.join(", ")}.`,
                                    nodeId: n.id,
                                });
                            }
                            else if (authType !== "bearer" && !authName) {
                                issues.push({
                                    level: "error",
                                    code: "http_action_auth_name_missing",
                                    message: `Action "${label(n)}" uses a ${authType} credential but has no ${authType === "query" ? "query parameter" : "header"} name.`,
                                    nodeId: n.id,
                                });
                            }
                        }
                        else if (authType !== "bearer" || authName) {
                            // Auth partly filled in but no secret chosen: silently sending an
                            // unauthenticated request is worse than saying so.
                            issues.push({
                                level: "error",
                                code: "http_action_auth_secret_missing",
                                message: `Action "${label(n)}" has credential settings but no secret selected.`,
                                nodeId: n.id,
                            });
                        }
                    }
                    if (isWholeUrlVariable) {
                        issues.push({
                            level: "warning",
                            code: "http_action_url_dynamic_host",
                            message: `Action "${label(n)}" builds its whole URL from a variable, so the host is only known at run time. It must resolve to a public HTTPS address or the step fails.`,
                            nodeId: n.id,
                        });
                    }
                }
                const configuredMethod = savedActionType === "http_get"
                    ? "GET"
                    : savedActionType === "http_post"
                        ? "POST"
                        : String(config.method ?? "POST").toUpperCase();
                if (actionType === "http_request" &&
                    !HTTP_REQUEST_METHODS.includes(configuredMethod)) {
                    issues.push({
                        level: "error",
                        code: "http_request_method_invalid",
                        message: `Action "${label(n)}" has an unsupported HTTP method "${configuredMethod}".`,
                        nodeId: n.id,
                    });
                }
                const configuredResponseType = String(config.responseType ?? "auto").toLowerCase();
                if (actionType === "http_request" &&
                    !HTTP_RESPONSE_TYPES.includes(configuredResponseType)) {
                    issues.push({
                        level: "error",
                        code: "http_response_type_invalid",
                        message: `Action "${label(n)}" has an unsupported response type "${configuredResponseType}".`,
                        nodeId: n.id,
                    });
                }
                if (actionType === "http_request" &&
                    (configuredResponseType === "pdf" || configuredResponseType === "image") &&
                    !nodes.some((node) => node.type === "agent")) {
                    issues.push({
                        level: "error",
                        code: "http_file_response_requires_agent",
                        message: `Action "${label(n)}" needs an Agent node because ${configuredResponseType.toUpperCase()} responses are saved to the assigned agent's workspace.`,
                        nodeId: n.id,
                    });
                }
                if (config.body != null && str(config.body).trim()) {
                    try {
                        JSON.parse(str(config.body));
                    }
                    catch {
                        issues.push({
                            level: "error",
                            code: "http_request_body_invalid",
                            message: `Action "${label(n)}" needs a valid JSON body template.`,
                            nodeId: n.id,
                        });
                    }
                }
                if (actionType === "wait_for_email_reply" &&
                    !str(config.messageId) &&
                    !str(config.conversationId) &&
                    !str(config.internetMessageId)) {
                    issues.push({
                        level: "error",
                        code: "email_wait_missing_identity",
                        message: `Action "${label(n)}" needs a message id, conversation id, or internet message id.`,
                        nodeId: n.id,
                    });
                }
                if (actionType === "wait_for_email_reply" && !str(config.deadlineAt)) {
                    const waitDuration = Number(config.waitDuration);
                    const waitUnit = str(config.waitUnit);
                    if (!Number.isInteger(waitDuration) ||
                        waitDuration <= 0 ||
                        !["minutes", "hours", "days"].includes(waitUnit)) {
                        issues.push({
                            level: "error",
                            code: "email_wait_duration_invalid",
                            message: `Action "${label(n)}" needs a positive wait duration and a unit of minutes, hours, or days.`,
                            nodeId: n.id,
                        });
                    }
                }
            }
        }
        if (n.type === "condition") {
            const outgoing = edges.filter((e) => e.source === n.id);
            const data = n.data;
            if (data.mode === "multiple") {
                const choices = data.choices ?? [];
                const choiceIds = new Set(choices.map((choice) => choice.id));
                const defaults = choices.filter((choice) => choice.isDefault);
                if (choices.length < 2) {
                    issues.push({
                        level: "error",
                        code: "condition_choices_missing",
                        message: `Condition "${label(n)}" needs at least two choices.`,
                        nodeId: n.id,
                    });
                }
                if (choiceIds.size !== choices.length) {
                    issues.push({
                        level: "error",
                        code: "condition_choice_ids_duplicate",
                        message: `Condition "${label(n)}" has duplicate choice IDs.`,
                        nodeId: n.id,
                    });
                }
                if (defaults.length !== 1) {
                    issues.push({
                        level: "error",
                        code: "condition_default_choice_invalid",
                        message: `Condition "${label(n)}" needs exactly one Otherwise choice.`,
                        nodeId: n.id,
                    });
                }
                for (const choice of choices) {
                    if (!outgoing.some((edge) => edge.sourceHandle === choice.id)) {
                        issues.push({
                            level: "warning",
                            code: "condition_choice_unconnected",
                            message: `Choice "${choice.label}" in condition "${label(n)}" is not connected.`,
                            nodeId: n.id,
                        });
                    }
                }
                for (const edge of outgoing) {
                    if (edge.sourceHandle && !choiceIds.has(edge.sourceHandle)) {
                        issues.push({
                            level: "error",
                            code: "condition_choice_edge_unknown",
                            message: `Condition "${label(n)}" has an edge for an unknown choice.`,
                            nodeId: n.id,
                        });
                    }
                }
                continue;
            }
            const hasYes = outgoing.some((e) => e.sourceHandle === "yes");
            const hasNo = outgoing.some((e) => e.sourceHandle === "no");
            if (!hasYes && !hasNo) {
                issues.push({
                    level: "warning",
                    code: "condition_no_branches",
                    message: `Condition "${label(n)}" has no yes/no branches — the run will end here.`,
                    nodeId: n.id,
                });
            }
            else if (!hasNo) {
                issues.push({
                    level: "warning",
                    code: "condition_no_no_branch",
                    message: `Condition "${label(n)}" has no "no" branch — a false result ends the run.`,
                    nodeId: n.id,
                });
            }
        }
        if (n.type === "decision") {
            issues.push(...validateDecisionNode(n, edges));
        }
    }
    // Reachability + safe-loop validation from the (first) trigger.
    if (triggers[0]) {
        const start = triggers[0].id;
        const adjacency = new Map();
        for (const e of edges) {
            if (!adjacency.has(e.source))
                adjacency.set(e.source, []);
            adjacency.get(e.source).push(e.target);
        }
        // Reachable set (BFS).
        const reachable = new Set([start]);
        const queue = [start];
        while (queue.length) {
            const cur = queue.shift();
            for (const next of adjacency.get(cur) ?? []) {
                if (!reachable.has(next)) {
                    reachable.add(next);
                    queue.push(next);
                }
            }
        }
        for (const n of nodes) {
            if (n.type === "trigger")
                continue;
            if (!reachable.has(n.id)) {
                issues.push({
                    level: "warning",
                    code: "unreachable_node",
                    message: `Node "${label(n)}" can't be reached from the trigger and will never run.`,
                    nodeId: n.id,
                });
            }
        }
        // Find strongly connected components. A cyclic component is safe only
        // when a condition inside it controls a branch that leaves it.
        let nextIndex = 0;
        const indexes = new Map();
        const lowLinks = new Map();
        const componentStack = [];
        const onStack = new Set();
        const components = [];
        const visit = (nodeId) => {
            indexes.set(nodeId, nextIndex);
            lowLinks.set(nodeId, nextIndex);
            nextIndex += 1;
            componentStack.push(nodeId);
            onStack.add(nodeId);
            for (const targetId of adjacency.get(nodeId) ?? []) {
                if (!nodeById.has(targetId))
                    continue;
                if (!indexes.has(targetId)) {
                    visit(targetId);
                    lowLinks.set(nodeId, Math.min(lowLinks.get(nodeId), lowLinks.get(targetId)));
                }
                else if (onStack.has(targetId)) {
                    lowLinks.set(nodeId, Math.min(lowLinks.get(nodeId), indexes.get(targetId)));
                }
            }
            if (lowLinks.get(nodeId) !== indexes.get(nodeId))
                return;
            const component = [];
            let memberId;
            do {
                memberId = componentStack.pop();
                if (!memberId)
                    break;
                onStack.delete(memberId);
                component.push(memberId);
            } while (memberId !== nodeId);
            components.push(component);
        };
        for (const n of nodes) {
            if (!indexes.has(n.id))
                visit(n.id);
        }
        for (const component of components) {
            const members = new Set(component);
            const isCycle = component.length > 1 ||
                edges.some((edge) => edge.source === component[0] && edge.target === component[0]);
            if (!isCycle)
                continue;
            // A decision node's branches are as good a loop exit as a condition's:
            // both are server-evaluated and pick exactly one outgoing handle.
            const controlledExit = edges.some((edge) => {
                if (!members.has(edge.source) || members.has(edge.target))
                    return false;
                const sourceNode = nodeById.get(edge.source);
                return ((sourceNode?.type === "condition" || sourceNode?.type === "decision") &&
                    Boolean(edge.sourceHandle));
            });
            const loopLabel = component
                .map((id) => label(nodeById.get(id)))
                .join(", ");
            const conditionId = component.find((id) => nodeById.get(id)?.type === "condition" || nodeById.get(id)?.type === "decision");
            if (!controlledExit) {
                issues.push({
                    level: "error",
                    code: "loop_without_exit",
                    message: `Loop "${loopLabel}" needs a condition branch that exits the loop.`,
                    nodeId: conditionId ?? component[0],
                });
            }
            else {
                issues.push({
                    level: "warning",
                    code: "loop_detected",
                    message: `Loop "${loopLabel}" is allowed because it has a condition-controlled exit. Runs stop automatically after ${MAX_WORKFLOW_STEP_EXECUTIONS} step executions.`,
                    nodeId: conditionId ?? component[0],
                });
            }
        }
    }
    return issues;
}
/**
 * Decision-node rules. Errors block a run; warnings are advisory. The handle
 * set is derived by decisionValueHandles so the canvas, this validator and
 * the runner can never disagree about which edges are legal.
 */
function validateDecisionNode(n, edges) {
    const issues = [];
    const data = (n.data ?? {});
    const outgoing = edges.filter((e) => e.source === n.id);
    const name = label(n);
    const state = data.state;
    const stateKeys = state && typeof state === "object" && !Array.isArray(state)
        ? Object.entries(state).filter(([, v]) => str(v))
        : [];
    if (stateKeys.length === 0) {
        issues.push({
            level: "error",
            code: "decision_state_missing",
            message: `Decision "${name}" has no state — add at least one named input for it to look at.`,
            nodeId: n.id,
        });
    }
    const questions = Array.isArray(data.questions) ? data.questions : [];
    if (questions.length === 0) {
        issues.push({
            level: "error",
            code: "decision_questions_missing",
            message: `Decision "${name}" has no questions.`,
            nodeId: n.id,
        });
        return issues;
    }
    const ids = questions.map((q) => str(q.id));
    if (new Set(ids).size !== ids.length || ids.some((id) => !id)) {
        issues.push({
            level: "error",
            code: "decision_question_ids_invalid",
            message: `Decision "${name}" has questions with missing or duplicate ids.`,
            nodeId: n.id,
        });
    }
    for (const q of questions) {
        const qid = str(q.id) || "?";
        if (!str(q.instructions)) {
            issues.push({
                level: "error",
                code: "decision_question_instructions_missing",
                message: `Question "${qid}" in decision "${name}" has no instructions.`,
                nodeId: n.id,
            });
        }
        if (q.type === "choice") {
            const options = Array.isArray(q.options) ? q.options : [];
            const bound = str(q.optionsFrom);
            if (!bound && options.length < 2) {
                issues.push({
                    level: "error",
                    code: "decision_choice_options_missing",
                    message: `Choice question "${qid}" in decision "${name}" needs at least two options, or optionsFrom bound to an earlier step.`,
                    nodeId: n.id,
                });
            }
            if (bound && !(bound.startsWith("steps.") || bound.startsWith("inputs."))) {
                issues.push({
                    level: "error",
                    code: "decision_options_from_invalid",
                    message: `Question "${qid}" in decision "${name}" has an optionsFrom path that must start with "steps." or "inputs.".`,
                    nodeId: n.id,
                });
            }
            const values = options.map((o) => str(o?.value));
            if (values.some((v) => !v) || new Set(values).size !== values.length) {
                issues.push({
                    level: "error",
                    code: "decision_choice_values_invalid",
                    message: `Choice question "${qid}" in decision "${name}" has options with missing or duplicate values.`,
                    nodeId: n.id,
                });
            }
            if (values.some((v) => DECISION_EXTRA_BRANCHES.includes(v))) {
                issues.push({
                    level: "error",
                    code: "decision_choice_value_reserved",
                    message: `Choice question "${qid}" in decision "${name}" uses a reserved option value (${DECISION_EXTRA_BRANCHES.join(", ")}).`,
                    nodeId: n.id,
                });
            }
        }
        else if (q.type === "score") {
            const levels = Array.isArray(q.levels) ? q.levels : [];
            if (levels.length < 2) {
                issues.push({
                    level: "error",
                    code: "decision_score_levels_missing",
                    message: `Score question "${qid}" in decision "${name}" needs at least two levels.`,
                    nodeId: n.id,
                });
            }
        }
        else if (q.type !== "noul") {
            issues.push({
                level: "error",
                code: "decision_question_type_invalid",
                message: `Question "${qid}" in decision "${name}" has an unknown type "${String(q.type)}". Use ${DECISION_QUESTION_TYPES.join(", ")}.`,
                nodeId: n.id,
            });
        }
    }
    const route = (data.route ?? {});
    const routed = decisionRoutedQuestion(data);
    if (str(route.question) && !routed) {
        issues.push({
            level: "error",
            code: "decision_route_question_unknown",
            message: `Decision "${name}" routes on question "${str(route.question)}", which does not exist.`,
            nodeId: n.id,
        });
        return issues;
    }
    if (routed?.type === "choice" && str(routed.optionsFrom)) {
        issues.push({
            level: "error",
            code: "decision_route_dynamic_options",
            message: `Decision "${name}" routes on a question whose options come from an earlier step, so its branches cannot be known when the workflow is saved. Route on a question with fixed options, or branch with a Condition on steps.${n.id}.outputs.answers.<id>.value.`,
            nodeId: n.id,
        });
        return issues;
    }
    const valueHandles = decisionValueHandles(data);
    const allowed = new Set([...valueHandles, ...DECISION_EXTRA_BRANCHES]);
    for (const handle of valueHandles) {
        if (!outgoing.some((e) => e.sourceHandle === handle)) {
            issues.push({
                level: "warning",
                code: "decision_branch_unconnected",
                message: `Branch "${handle}" of decision "${name}" is not connected — a run that takes it ends there.`,
                nodeId: n.id,
            });
        }
    }
    for (const e of outgoing) {
        if (!e.sourceHandle || !allowed.has(e.sourceHandle)) {
            issues.push({
                level: "error",
                code: "decision_edge_unknown_branch",
                message: `Decision "${name}" has an edge "${e.id}" whose sourceHandle "${e.sourceHandle ?? "(none)"}" is not one of its branches (${[...allowed].join(", ")}).`,
                edgeId: e.id,
                nodeId: n.id,
            });
        }
    }
    const fallback = str(route.fallbackBranch);
    if (fallback && !valueHandles.includes(fallback)) {
        issues.push({
            level: "error",
            code: "decision_fallback_invalid",
            message: `Decision "${name}" has fallbackBranch "${fallback}", which is not one of its option branches (${valueHandles.join(", ")}).`,
            nodeId: n.id,
        });
    }
    if (!fallback) {
        const unconnectedExtras = DECISION_EXTRA_BRANCHES.filter((h) => !outgoing.some((e) => e.sourceHandle === h));
        if (unconnectedExtras.length > 0) {
            issues.push({
                level: "warning",
                code: "decision_fallback_missing",
                message: `Decision "${name}" has no fallbackBranch and its ${unconnectedExtras.join(", ")} branch${unconnectedExtras.length > 1 ? "es are" : " is"} not connected — a run taking one of them ends there (and a provider outage would stop the workflow). Set route.fallbackBranch to the safe path.`,
                nodeId: n.id,
            });
        }
    }
    return issues;
}
/** Convenience: true when the graph has no blocking errors. */
export function isWorkflowGraphRunnable(graph) {
    return !validateWorkflowGraph(graph).some((i) => i.level === "error");
}
function str(v) {
    return typeof v === "string" ? v.trim() : "";
}
function label(n) {
    const data = (n.data ?? {});
    return data.label && data.label.trim() ? data.label : n.id;
}
//# sourceMappingURL=workflow-capabilities.js.map